import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import timezone from 'dayjs/plugin/timezone'

dayjs.extend(utc)
dayjs.extend(timezone)

import type { GeneratedGame } from '../components/GeneratedGamesSection'
import type { DivisionItem } from '../hooks/useSchedulerDivisions'
import type { DivisionRow, TeamRow } from '../components/ScheduleReviewTable'
import type {
  FillCompletenessStatus,
  SchedulerRequestGame,
  SchedulerViolation,
} from '../hooks/useSchedulerGeneration'
import { classifyViolation, dedupeViolations } from './violations'

const SCORE_THRESHOLDS = { EXCELLENT: 96, GOOD: 90 } as const

/** Conflict/advisory counts + fill verdict that cap the score word (shared policy). */
export interface ScoreLabelCounts {
  conflicts?: number
  advisories?: number
  /**
   * The fill-completeness verdict (fill divisions only). `avoidable_underfill`
   * (legal capacity left unused) and `unknown` (could not be verified) both
   * BLOCK "Excellent" — a fill that leaves fillable slots empty is never
   * Excellent even with zero placed-game violations. `complete` /
   * `fill_limited_by_setup` never cap. Null means a NON-fill division (correctly
   * no cap) — api2 lifts a KNOWN fill division's missing verdict (pre-feature /
   * deploy-skew) to `unknown` upstream, so a fill division never reaches here
   * with a null verdict and non-fill schedules keep the pure numeric mapping.
   */
  fillCompletenessStatus?: FillCompletenessStatus | null
}

/**
 * Get the score label from a satisfaction score, made honest about the badges
 * shown beside it:
 *   - any open conflict        → caps at "NEEDS IMPROVEMENT" (regardless of %)
 *   - any open advisory        → caps at "GOOD" (never "EXCELLENT")
 *   - avoidable underfill /    → caps at "GOOD" (the fill contract: legal unused
 *     unverifiable fill           capacity, or an unverifiable fill, is never
 *                                 Excellent)
 *   - clean (0/0, complete)    → the raw numeric mapping (≥96 EXCELLENT, ≥90 GOOD)
 * Info never caps, so it is intentionally not part of `opts`. `opts` is optional
 * so existing callers keep the pure numeric mapping.
 */
export function getScoreLabel(
  score: number | null,
  opts?: ScoreLabelCounts
): string {
  if (score === null) {
    return '--'
  }
  const conflicts = opts?.conflicts ?? 0
  const advisories = opts?.advisories ?? 0
  // Avoidable underfill / unverifiable fill block Excellent (the fill contract).
  // Null = non-fill (api2 lifts a pre-feature fill division's missing verdict to
  // `unknown` upstream); null and complete / fill_limited_by_setup never cap —
  // only the two explicit "not Excellent" verdicts do.
  const fillBlocksExcellent =
    opts?.fillCompletenessStatus === 'avoidable_underfill' ||
    opts?.fillCompletenessStatus === 'unknown'
  if (conflicts > 0) {
    return 'NEEDS IMPROVEMENT'
  }
  if (advisories > 0 || fillBlocksExcellent) {
    return score >= SCORE_THRESHOLDS.GOOD ? 'GOOD' : 'NEEDS IMPROVEMENT'
  }
  if (score >= SCORE_THRESHOLDS.EXCELLENT) {
    return 'EXCELLENT'
  }
  if (score >= SCORE_THRESHOLDS.GOOD) {
    return 'GOOD'
  }
  return 'NEEDS IMPROVEMENT'
}

/**
 * Attribute conflicts/advisories to divisions for the per-division score cap.
 * Team-scoped violations attribute to their teams' home divisions FIRST — the
 * solver's `affected_game_numbers` can be over-broad (span other divisions), so
 * a per-team advisory must only cap its own team's division, never an unrelated
 * one that merely shares the week. Game numbers are a fallback, used only when a
 * violation carries no resolvable team scope (e.g. venue/global constraints).
 * Prevented/info are excluded by the shared policy.
 */
function countViolationsByDivision(
  violations: SchedulerViolation[],
  gameDivisionByNumber: Map<number, string>,
  teamDivisionByTeamUid: Map<string, string>
): Map<string, ScoreLabelCounts> {
  const byDivision = new Map<
    string,
    { conflicts: number; advisories: number }
  >()
  for (const v of dedupeViolations(violations)) {
    const cls = classifyViolation(v.severity, v.is_prevented)
    if (cls !== 'conflict' && cls !== 'advisory') {
      continue
    }
    const divs = new Set<string>()
    // Team scope is authoritative when present (context.team_uid + affected
    // teams) — a per-team violation only caps its own team's division.
    const ctx = v.context ?? {}
    const ctxTeam = typeof ctx.team_uid === 'string' ? ctx.team_uid : null
    const teamUids = ctxTeam
      ? [ctxTeam, ...(v.affected_team_uids ?? [])]
      : v.affected_team_uids ?? []
    for (const t of teamUids) {
      const d = teamDivisionByTeamUid.get(t)
      if (d) {
        divs.add(d)
      }
    }
    // Fall back to game-number divisions only when there's no team scope — the
    // solver's affected_game_numbers can be over-broad (span other divisions).
    if (divs.size === 0) {
      for (const g of v.affected_game_numbers ?? []) {
        const d = gameDivisionByNumber.get(g)
        if (d) {
          divs.add(d)
        }
      }
    }
    for (const d of divs) {
      const entry = byDivision.get(d) ?? { conflicts: 0, advisories: 0 }
      if (cls === 'conflict') {
        entry.conflicts++
      } else {
        entry.advisories++
      }
      byDivision.set(d, entry)
    }
  }
  return byDivision
}

/**
 * Transform API games into GeneratedGame[] for GeneratedGamesSection
 */
export function transformGamesToDisplay(
  games: SchedulerRequestGame[],
  divisionItems: DivisionItem[]
): GeneratedGame[] {
  return games.map((game) => {
    const divItem = divisionItems.find((d) => d.id === game.division_uid)
    return {
      id: game.id,
      gameNumber: game.game_number,
      globalGameNumber: game.global_game_number ?? null,
      date: dayjs
        .utc(game.starts_at)
        .tz(game.venue?.timezone || 'UTC')
        .format('ddd, MMM D, YYYY'),
      time: dayjs
        .utc(game.starts_at)
        .tz(game.venue?.timezone || 'UTC')
        .format('h:mm A'),
      location: game.venue?.name ?? 'Unknown Venue',
      locationId: game.venue?.id,
      surface: game.facility?.name ?? 'Unknown Surface',
      surfaceId: game.facility?.id,
      divisionId: game.division_uid,
      division: divItem?.name ?? game.division_uid,
      homeTeam: game.home_team?.name ?? 'TBD',
      homeTeamId: game.home_team?.id,
      awayTeam: game.visiting_team?.name ?? 'TBD',
      awayTeamId: game.visiting_team?.id,
      hasError: false,
      hasWarning: game.approval_status === 'rejected',
      startsAt: game.starts_at,
      endsAt: game.ends_at,
      timezone: game.venue?.timezone || undefined,
    }
  })
}

/**
 * Transform API games into DivisionRow[] for ScheduleReviewTable
 */
export function transformGamesToDivisionRows(
  games: SchedulerRequestGame[],
  divisionItems: DivisionItem[],
  satisfactionScore: number | null,
  violations: SchedulerViolation[] = [],
  gameDivisionByNumber: Map<number, string> = new Map(),
  teamDivisionByTeamUid: Map<string, string> = new Map(),
  // Per-division fill-completeness verdict (fill divisions only) so a division
  // that left legal capacity unused is never labelled "Excellent". A missing
  // entry / null is safe only for non-fill (or unclassified) divisions — api2
  // lifts a KNOWN fill division's pre-feature null to `unknown` upstream, so a
  // fill division never reaches here as null.
  fillStatusByDivision: Map<string, FillCompletenessStatus | null> = new Map()
): DivisionRow[] {
  // Per-division conflict/advisory counts, for the honest per-division score cap.
  const violationsByDivision = countViolationsByDivision(
    violations,
    gameDivisionByNumber,
    teamDivisionByTeamUid
  )

  // Group games by division_uid
  const byDivision = new Map<string, SchedulerRequestGame[]>()
  for (const game of games) {
    const key = game.division_uid
    if (!byDivision.has(key)) {
      byDivision.set(key, [])
    }
    byDivision.get(key)!.push(game)
  }

  const divisionRows: DivisionRow[] = []
  for (const [divUid, divGames] of byDivision) {
    // Find the division name from our divisions data
    const divItem = divisionItems.find((d) => d.id === divUid)
    const divName = divItem?.name ?? divUid

    // Count games per team
    const teamGameCounts = new Map<
      string,
      { home: number; visitor: number; name: string; startMinutes: number[] }
    >()
    for (const g of divGames) {
      if (g.home_team) {
        const existing = teamGameCounts.get(g.home_team.id) ?? {
          home: 0,
          visitor: 0,
          name: g.home_team.name,
          startMinutes: [],
        }
        existing.home++
        existing.name = g.home_team.name
        if (g.starts_at) {
          const d = dayjs.utc(g.starts_at).tz(g.venue?.timezone || 'UTC')
          existing.startMinutes.push(d.hour() * 60 + d.minute())
        }
        teamGameCounts.set(g.home_team.id, existing)
      }
      if (g.visiting_team) {
        const existing = teamGameCounts.get(g.visiting_team.id) ?? {
          home: 0,
          visitor: 0,
          name: g.visiting_team.name,
          startMinutes: [],
        }
        existing.visitor++
        existing.name = g.visiting_team.name
        if (g.starts_at) {
          const d = dayjs.utc(g.starts_at).tz(g.venue?.timezone || 'UTC')
          existing.startMinutes.push(d.hour() * 60 + d.minute())
        }
        teamGameCounts.set(g.visiting_team.id, existing)
      }
    }

    const formatMinutes = (min: number) =>
      dayjs
        .utc()
        .hour(Math.floor(min / 60))
        .minute(min % 60)
        .format('h:mm A')

    const teamRows: TeamRow[] = []
    for (const [teamId, counts] of teamGameCounts) {
      let avgStartTime = '--'
      let lateStartTime = '--'
      if (counts.startMinutes.length > 0) {
        const sum = counts.startMinutes.reduce((a, b) => a + b, 0)
        avgStartTime = formatMinutes(
          Math.round(sum / counts.startMinutes.length)
        )
        lateStartTime = formatMinutes(Math.max(...counts.startMinutes))
      }
      teamRows.push({
        id: teamId,
        name: counts.name,
        games: counts.home + counts.visitor,
        home: counts.home,
        visitor: counts.visitor,
        avgStartTime,
        lateStartTime,
        exceptions: 0,
      })
    }

    const totalTeams = teamRows.length
    const teamGamesSum = teamRows.reduce((sum, t) => sum + t.games, 0)
    const gamesPerTeam =
      totalTeams > 0 ? Math.round((teamGamesSum / totalTeams) * 10) / 10 : 0

    // Compute average start time from game starts_at
    let avgStartTimeStr = '--'
    if (divGames.length > 0) {
      let totalMinutes = 0
      let validCount = 0
      for (const g of divGames) {
        if (g.starts_at) {
          const d = dayjs.utc(g.starts_at).tz(g.venue?.timezone || 'UTC')
          totalMinutes += d.hour() * 60 + d.minute()
          validCount++
        }
      }
      if (validCount > 0) {
        const avgMin = Math.round(totalMinutes / validCount)
        const hours = Math.floor(avgMin / 60)
        const minutes = avgMin % 60
        avgStartTimeStr = dayjs
          .utc()
          .hour(hours)
          .minute(minutes)
          .format('h:mm A')
      }
    }

    // Compute division status from games
    let divStatus: 'pending' | 'draft' | 'approved' | 'applied' = 'pending'
    if (divGames.length > 0) {
      const allApplied = divGames.every((g) => g.is_applied === true)
      if (allApplied) {
        divStatus = 'applied'
      } else {
        const allApproved = divGames.every(
          (g) => g.approval_status === 'approved'
        )
        divStatus = allApproved ? 'approved' : 'draft'
      }
    }

    divisionRows.push({
      id: divUid,
      name: divName,
      games: divGames.length,
      gamesPerTeam,
      avgStartTime: avgStartTimeStr,
      players: divItem?.playersCount ?? 0,
      teams: totalTeams,
      divisionScore: getScoreLabel(satisfactionScore, {
        ...violationsByDivision.get(divUid),
        fillCompletenessStatus: fillStatusByDivision.get(divUid) ?? null,
      }),
      status: divStatus,
      teamRows,
    })
  }

  return divisionRows
}
